import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { 
  Ship, 
  Plane, 
  Truck, 
  Package, 
  Warehouse, 
  ArrowRight,
  Globe,
  Shield,
  Clock
} from "lucide-react";
import { Button } from "@/components/ui/button";

// Animation Variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

// Hero Section
const HeroSection = () => {
  return (
    <section className="relative min-h-screen flex items-center" data-testid="hero-section">
      <div 
        className="absolute inset-0 bg-cover bg-center"
        style={{ 
          backgroundImage: `url('https://images.unsplash.com/photo-1770838517148-caacddedac1a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHw0fHxjYXJnbyUyMHNoaXAlMjBjb250YWluZXIlMjBzZWElMjBmcmVpZ2h0fGVufDB8fHx8MTc3MzUzODM5MHww&ixlib=rb-4.1.0&q=85')` 
        }}
      />
      <div className="hero-overlay absolute inset-0" />
      
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-20">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
          className="max-w-3xl"
        >
          <motion.p 
            variants={fadeInUp}
            className="uppercase tracking-[0.2em] text-xs font-bold text-[#F59E0B] mb-4"
          >
            Global Logistics Solutions
          </motion.p>
          
          <motion.h1 
            variants={fadeInUp}
            className="heading-font text-4xl sm:text-5xl lg:text-6xl font-bold text-white uppercase tracking-tight leading-none mb-6"
          >
            Delivering Excellence <br />
            <span className="text-[#F59E0B]">Across The Globe</span>
          </motion.h1>
          
          <motion.p 
            variants={fadeInUp}
            className="text-lg text-slate-300 leading-relaxed mb-8 max-w-xl"
          >
            Your trusted partner for sea, air, and land freight services. 
            We ensure secure, efficient, and timely delivery of your cargo worldwide.
          </motion.p>
          
          <motion.div 
            variants={fadeInUp}
            className="flex flex-col sm:flex-row gap-4"
          >
            <Link to="/contact">
              <Button 
                className="bg-[#F59E0B] hover:bg-[#D97706] text-black font-bold uppercase tracking-wide h-12 px-8 rounded-sm transition-all duration-300 hover:scale-[1.02]"
                data-testid="hero-contact-btn"
              >
                Contact Us <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
            <Link to="/services">
              <Button 
                className="border-2 border-white text-white bg-transparent hover:bg-white hover:text-[#0B2545] font-bold uppercase tracking-wide h-12 px-8 rounded-sm transition-all duration-300"
                data-testid="hero-services-btn"
              >
                Our Services
              </Button>
            </Link>
          </motion.div>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.6 }}
          className="grid grid-cols-3 gap-6 mt-16 max-w-2xl"
        >
          <div className="stat-item text-center sm:text-left">
            <p className="heading-font text-3xl sm:text-4xl font-bold text-[#F59E0B]">50+</p>
            <p className="text-sm text-slate-300 mt-1">Countries Served</p>
          </div>
          <div className="stat-item text-center sm:text-left">
            <p className="heading-font text-3xl sm:text-4xl font-bold text-[#F59E0B]">24/7</p>
            <p className="text-sm text-slate-300 mt-1">Support Available</p>
          </div>
          <div className="stat-item text-center sm:text-left">
            <p className="heading-font text-3xl sm:text-4xl font-bold text-[#F59E0B]">15K+</p>
            <p className="text-sm text-slate-300 mt-1">Shipments Yearly</p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Services Preview
const servicesPreview = [
  { id: "sea", title: "Sea Freight", icon: Ship, description: "Cost-effective ocean shipping for bulk cargo" },
  { id: "air", title: "Air Freight", icon: Plane, description: "Express air cargo for time-sensitive shipments" },
  { id: "land", title: "Land Freight", icon: Truck, description: "Reliable road transportation across Europe" },
];

const ServicesPreviewSection = () => {
  return (
    <section className="py-24 md:py-32 bg-white" data-testid="services-preview-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
        >
          <motion.div variants={fadeInUp} className="text-center mb-16">
            <p className="uppercase tracking-[0.2em] text-xs font-bold text-[#F59E0B] mb-4">What We Offer</p>
            <h2 className="heading-font text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0B2545] uppercase tracking-tight mb-4">
              Our Core Services
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Comprehensive logistics solutions tailored to meet your unique shipping requirements
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {servicesPreview.map((service) => {
              const Icon = service.icon;
              return (
                <motion.div
                  key={service.id}
                  variants={fadeInUp}
                  className="service-card bg-white border border-slate-200 p-8 text-center"
                  data-testid={`service-preview-${service.id}`}
                >
                  <div className="w-16 h-16 bg-slate-100 flex items-center justify-center mx-auto mb-6">
                    <Icon className="w-8 h-8 text-[#0B2545]" />
                  </div>
                  <h3 className="heading-font text-xl font-bold text-[#0B2545] uppercase tracking-tight mb-3">
                    {service.title}
                  </h3>
                  <p className="text-slate-600">{service.description}</p>
                </motion.div>
              );
            })}
          </div>

          <motion.div variants={fadeInUp} className="text-center mt-12">
            <Link to="/services">
              <Button className="btn-primary h-12 px-8 rounded-sm" data-testid="view-all-services-btn">
                View All Services <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// Why Choose Us Section
const WhyChooseUsSection = () => {
  const features = [
    { icon: Globe, title: "Global Network", text: "Extensive partner network across 50+ countries for seamless international shipping" },
    { icon: Shield, title: "Secure Handling", text: "Full insurance coverage and advanced security protocols for your valuable cargo" },
    { icon: Clock, title: "On-Time Delivery", text: "98% on-time delivery rate with real-time tracking and proactive communication" }
  ];

  return (
    <section className="py-24 md:py-32 bg-slate-50" data-testid="why-choose-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
          className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center"
        >
          <div>
            <motion.p 
              variants={fadeInUp}
              className="uppercase tracking-[0.2em] text-xs font-bold text-[#F59E0B] mb-4"
            >
              Why Choose Us
            </motion.p>
            <motion.h2 
              variants={fadeInUp}
              className="heading-font text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0B2545] uppercase tracking-tight mb-6"
            >
              Your Cargo, Our Priority
            </motion.h2>
            <motion.p 
              variants={fadeInUp}
              className="text-slate-600 leading-relaxed mb-8"
            >
              At Sky Cargo Delivery, we understand that every shipment matters. Our dedicated team works 
              around the clock to ensure your cargo reaches its destination safely and on time.
            </motion.p>

            <motion.div variants={fadeInUp} className="space-y-6">
              {features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <div key={index} className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#0B2545] flex items-center justify-center flex-shrink-0">
                      <Icon className="w-6 h-6 text-[#F59E0B]" />
                    </div>
                    <div>
                      <h4 className="font-bold text-[#0B2545] mb-1">{feature.title}</h4>
                      <p className="text-sm text-slate-600">{feature.text}</p>
                    </div>
                  </div>
                );
              })}
            </motion.div>

            <motion.div variants={fadeInUp} className="mt-8">
              <Link to="/about">
                <Button className="btn-secondary h-12 px-8 rounded-sm" data-testid="learn-more-btn">
                  Learn More About Us
                </Button>
              </Link>
            </motion.div>
          </div>

          <motion.div variants={fadeInUp} className="relative">
            <div className="aspect-[4/3] overflow-hidden">
              <img 
                src="https://images.unsplash.com/photo-1768796373360-95d80c5830fb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjB3YXJlaG91c2UlMjBkaXN0cmlidXRpb24lMjBjZW50ZXIlMjBpbnRlcmlvcnxlbnwwfHx8fDE3NzM1MzgzOTN8MA&ixlib=rb-4.1.0&q=85"
                alt="Warehouse Operations"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-[#F59E0B] hidden lg:block" />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// CTA Section
const CTASection = () => {
  return (
    <section className="py-20 bg-[#0B2545]" data-testid="cta-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="text-center"
        >
          <motion.h2 
            variants={fadeInUp}
            className="heading-font text-3xl sm:text-4xl font-bold text-white uppercase tracking-tight mb-4"
          >
            Ready to Ship Your Cargo?
          </motion.h2>
          <motion.p 
            variants={fadeInUp}
            className="text-slate-300 mb-8 max-w-xl mx-auto"
          >
            Get in touch with our team for a free quote. We'll help you find the best shipping solution for your needs.
          </motion.p>
          <motion.div variants={fadeInUp}>
            <Link to="/contact">
              <Button 
                className="bg-[#F59E0B] hover:bg-[#D97706] text-black font-bold uppercase tracking-wide h-12 px-8 rounded-sm transition-all duration-300"
                data-testid="cta-contact-btn"
              >
                Get Free Quote <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

const HomePage = () => {
  return (
    <div data-testid="home-page">
      <HeroSection />
      <ServicesPreviewSection />
      <WhyChooseUsSection />
      <CTASection />
    </div>
  );
};

export default HomePage;
